import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../store';
import { firestore, firestoreLiveDoc, firestoreLiveQuery } from '../firebase';
import { ChatData, MessageData } from '../types';
import { createCrudActions } from '../utils/redux';

let cancelLiveChat: any;
let cancelLiveMessages: any;

interface ChatMessagesState {
  chat: ChatData | null;
  messages: MessageData[];
  chatLoading: boolean;
  messagesLoading: boolean;
}

const initialState: ChatMessagesState = {
  chat: null,
  messages: [],
  chatLoading: false,
  messagesLoading: false,
}


const messageActions = createCrudActions<MessageData, ChatMessagesState>({
  idKey: 'id',
  itemsKey: 'messages',
  sort: {
    field: 'updateTime',
    order: 'desc',
  }
});


const service = createSlice({
  name: 'chatMessages',
  initialState,
  reducers: {
    setChatLoading(state, action: PayloadAction<boolean>): ChatMessagesState {
      return { ...state, chatLoading: action.payload, }
    },
    setMessagesLoading(state, action: PayloadAction<boolean>): ChatMessagesState {
      return { ...state, messagesLoading: action.payload, }
    },
    setChatData(state, action: PayloadAction<ChatData | null>): ChatMessagesState {
      return { ...state, chat: action.payload, chatLoading: false, }
    },
    resetMessages(state): ChatMessagesState {
      return { ...state, messages: [], messagesLoading: false, }
    },

    onMessageAdd: messageActions.onAdd,
    onMessageAddMany: messageActions.onAddMany,
    onMessageUpdate: messageActions.onUpdate,
    onMessageDelete: messageActions.onDelete,
  }
});


const resetChat = (): AppThunk => async (dispatch, getState) => {
  cancelLiveChat && cancelLiveChat();
  dispatch(service.actions.setChatData(null));
}

const resetMessages = (): AppThunk => async (dispatch, getState) => {
  cancelLiveMessages && cancelLiveMessages();
  dispatch(service.actions.resetMessages());
}

const loadChat = (chatId: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setChatLoading(true));

  cancelLiveChat = firestoreLiveDoc<ChatData>({
    ref: firestore.collection('chats').doc(chatId),
    onGet: (data) => {
      dispatch(service.actions.setChatData(data));
      dispatch(service.actions.setChatLoading(false));
    },
    onUpdate: (data) => {
      dispatch(service.actions.setChatData(data));
    },
    onError: (err) => {
      console.error(err);
    }
  })
}

const loadMessages = (chatId: string): AppThunk => async (dispatch, getState) => {
  dispatch(service.actions.setMessagesLoading(true));

  cancelLiveMessages = firestoreLiveQuery<MessageData>({
    query: firestore.collection('chats').doc(chatId).collection('chat_messages').orderBy('createTime', 'desc').limit(200),
    onAdd: (item) => {
      dispatch(service.actions.onMessageAdd(item));
    },
    onUpdate: (item) => {
      dispatch(service.actions.onMessageUpdate(item));
    },
    onDelete: (item) => {
      dispatch(service.actions.onMessageDelete(item));
    },
    onInitial: (items) => {
      dispatch(service.actions.onMessageAddMany(items));
      dispatch(service.actions.setMessagesLoading(false));
    },
    skipInitialOnAdd: true,
  })
}



export const actions = {
  resetChat,
  resetMessages,
  loadChat,
  loadMessages,
}

export default service.reducer;